/* eslint-disable functional/prefer-tacit */
import * as dateFns from 'date-fns';

export const isToday = (value?: string | number | Date) => {
  const date = new Date(value ?? 0);

  return dateFns.isToday(date);
};

/**
 * Returns a new `Date` based on the supplied date string but without extra information (milliseconds, timezone, etc.)
 *
 * NOTE: If the provided value is not supported, `undefined` is returned
 *
 * Supported date string formats:
 *
 * - Any ISO date string:           2000-03-15T05:20:10...
 * - ISO date string without `T`:   2000-03-15 05:20:10...
 */
export const ignoreTimezone = (dateString: string) => {
  // leave out milliseconds, timezone and other extra information
  const dateStringWithoutExtras = dateString.slice(0, 19).replace(' ', 'T');
  const dateWithoutExtras = dateFns.parseISO(dateStringWithoutExtras);

  const hasValidDateStringLength = dateStringWithoutExtras.length === 19;
  const isValidDate =
    hasValidDateStringLength && dateFns.isValid(dateWithoutExtras);

  // ─────────────────────────────────────────────────────────────────

  if (!isValidDate) {
    // eslint-disable-next-line no-console
    console.error(`Unsupported date string ("${dateString}")`);

    return;
  }

  // ─────────────────────────────────────────────────────────────────

  return dateWithoutExtras;
};

export const getDay = (day: string) => {
  return switchcase<WeekdayTranslationKey>({
    '0': 'general.weekday.sunday',
    '1': 'general.weekday.monday',
    '2': 'general.weekday.tuesday',
    '3': 'general.weekday.wednesday',
    '4': 'general.weekday.thursday',
    '5': 'general.weekday.friday',
    '6': 'general.weekday.saturday',
  })('general.weekday.monday')(day);
};

export const getMonth = (month: string) => {
  return switchcase<MonthTranslationKey>({
    '0': 'general.month.january',
    '1': 'general.month.february',
    '2': 'general.month.march',
    '3': 'general.month.april',
    '4': 'general.month.may',
    '5': 'general.month.june',
    '6': 'general.month.july',
    '7': 'general.month.august',
    '8': 'general.month.september',
    '9': 'general.month.october',
    '10': 'general.month.november',
    '11': 'general.month.december',
  })('general.month.january')(month);
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export type WeekdayTranslationKey =
  | 'general.weekday.sunday'
  | 'general.weekday.monday'
  | 'general.weekday.tuesday'
  | 'general.weekday.wednesday'
  | 'general.weekday.thursday'
  | 'general.weekday.friday'
  | 'general.weekday.saturday';

export type MonthTranslationKey =
  | 'general.month.january'
  | 'general.month.february'
  | 'general.month.march'
  | 'general.month.april'
  | 'general.month.may'
  | 'general.month.june'
  | 'general.month.july'
  | 'general.month.august'
  | 'general.month.september'
  | 'general.month.october'
  | 'general.month.november'
  | 'general.month.december';

// ─────────────────────────────────────────────────────────────────────

function switchcase<T>(cases: Record<string, T>) {
  return (defaultCase: T) => (key: string) =>
    cases[key] ? cases[key] : defaultCase;
}
